/**
 * Ajuste manual de inventario del **kardex**.
 *
 * Es el modal `#modalAjusteInventario` de `views/kardex/index.php`: un movimiento
 * que no viene de una compra, de una salida de almacén ni del POS, y que el kardex
 * refleja como **AJUSTE** (sumando si es entrada y restando si es salida).
 *
 * Reglas heredadas: hay que decir el **tipo**, la **cantidad**, la **fecha** y el
 * **motivo**, y el ajuste queda ligado al producto del kardex que se está viendo (y a
 * su manguera, si el producto la tiene).
 *
 * Diferencias anotadas: la cantidad tiene que ser mayor que cero y el motivo no puede
 * quedar vacío (el original sólo lo pedía en el navegador), y la fecha se manda con
 * segundos, que es como la guarda la API.
 */

import { Save } from 'lucide-react';
import { useRef, useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { CampoFecha } from '@/components/ui/calendario';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { nuevaClaveDeEnvio } from '@/lib/clave-de-envio';
import { useGuardarAjusteKardex } from '../kardex.api';
import type { ProductoKardex, Valorizacion } from '../kardex.types';

const CONTROL =
  'h-10 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** La fecha de ahora en el formato que pide el campo (`AAAA-MM-DDTHH:mm`). */
function ahora(): string {
  const hoy = new Date();
  const dia = String(hoy.getDate()).padStart(2, '0');
  const mes = String(hoy.getMonth() + 1).padStart(2, '0');

  return `${hoy.getFullYear()}-${mes}-${dia}T${String(hoy.getHours()).padStart(2, '0')}:${String(
    hoy.getMinutes(),
  ).padStart(2, '0')}`;
}

export function KardexAjusteDialog({
  open,
  onClose,
  producto,
  valorizacion,
  onAviso,
}: {
  open: boolean;
  onClose: () => void;
  producto: ProductoKardex | undefined;
  /** Valorización que se estaba consultando, para volver a pedir el libro igual. */
  valorizacion: Valorizacion;
  onAviso: (mensaje: string) => void;
}) {
  const guardar = useGuardarAjusteKardex();

  const [tipo, setTipo] = useState<'entrada' | 'salida'>('entrada');
  const [cantidad, setCantidad] = useState('');
  const [fecha, setFecha] = useState(ahora);
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState<string | null>(null);

  // La clave del envío: si el navegador reintenta no se registra el ajuste dos veces.
  const claveDelAjuste = useRef(nuevaClaveDeEnvio());

  const limpiar = () => {
    setTipo('entrada');
    setCantidad('');
    setFecha(ahora());
    setMotivo('');
    setError(null);
  };

  const enviar = () => {
    setError(null);

    if (!producto) {
      setError('Elige antes el producto del kardex.');
      return;
    }

    const valor = Number(cantidad.replace(',', '.'));

    if (!Number.isFinite(valor) || valor <= 0) {
      setError('La cantidad tiene que ser mayor que cero.');
      return;
    }

    if (motivo.trim() === '') {
      setError('Escribe el motivo del ajuste.');
      return;
    }

    guardar.mutate(
      {
        producto_id: producto.producto_id,
        fuel_grade_id: producto.Id,
        tipo,
        cantidad: valor,
        motivo: motivo.trim(),
        // El campo del calendario no lleva segundos y la API los pide.
        fecha_ajuste: `${fecha.replace('T', ' ')}:00`,
        clave_idempotencia: claveDelAjuste.current,
      },
      {
        onSuccess: () => {
          claveDelAjuste.current = nuevaClaveDeEnvio();
          onAviso(
            `Ajuste de ${tipo} por ${valor.toLocaleString('es-PE')} registrado en ${producto.Name}.`,
          );
          limpiar();
          onClose();
        },
        onError: (fallo: unknown) =>
          setError(
            fallo instanceof Error ? fallo.message : 'No se pudo registrar el ajuste.',
          ),
      },
    );
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        limpiar();
        onClose();
      }}
      title="Ajuste de inventario"
      description="Registra un movimiento manual de stock: una entrada lo aumenta y una salida lo disminuye."
      size="md"
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => {
              limpiar();
              onClose();
            }}
          >
            Cancelar
          </Button>
          <Button
            leftIcon={<Save className="size-4" aria-hidden="true" />}
            loading={guardar.isPending}
            onClick={enviar}
          >
            Registrar ajuste
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        {error ? (
          <Alert variant="error" title="Revisa el ajuste">
            {error}
          </Alert>
        ) : null}

        <p className="rounded-card bg-ink-50/70 p-3 text-xs text-ink-600">
          El ajuste se registra sobre <strong>{producto?.Name ?? '— sin producto —'}</strong> con la
          valorización «{valorizacion === 'moving_average' ? 'costo promedio móvil' : 'costo fijo'}»
          que estás consultando, así que después hay que volver a pulsar <em>Consultar</em> para ver
          el saldo actualizado.
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
            Tipo de ajuste
            <Select
              value={tipo}
              onChange={(evento) => setTipo(evento.target.value as 'entrada' | 'salida')}
              className={CONTROL}
              aria-label="Tipo de ajuste"
            >
              <option value="entrada">Entrada (aumenta el stock)</option>
              <option value="salida">Salida (disminuye el stock)</option>
            </Select>
          </label>

          <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
            Cantidad
            <Input
              type="number"
              min="0.001"
              step="0.001"
              value={cantidad}
              containerClassName="w-full"
              aria-label="Cantidad"
              placeholder="0.000"
              onChange={(evento) => setCantidad(evento.target.value)}
            />
          </label>
        </div>

        <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
          Fecha del ajuste
          <CampoFecha
            tipo="datetime-local"
            value={fecha}
            onChange={(evento) => setFecha(evento.target.value)}
            aria-label="Fecha del ajuste"
          />
        </label>

        <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
          Motivo del ajuste
          <textarea
            className={`${CONTROL} h-auto min-h-20 py-2`}
            value={motivo}
            maxLength={255}
            aria-label="Motivo del ajuste"
            placeholder="Describe la razón del ajuste (conteo físico, merma, corrección…)"
            onChange={(evento) => setMotivo(evento.target.value)}
          />
        </label>
      </div>
    </Dialog>
  );
}
